import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { UnRx } from '@pebula/utils';
import { BorrowerApi } from '@sg/api/borrower-api';
import { tap } from 'rxjs/operators';
import { SgEntityBasedSession } from '../misc';
import { CollateralSpecificMatch, CollateralTypeDto, CollateralTypeEnum, LocateMatch } from '../models';
import { convertBpsToPercentage } from '../units-conversion-utils';
import {
  groupAvailabilitySplitByCollateralType,
  isCashCollateral,
  isCashNonCashCollateralType,
} from './collateral.utils';

// Re-exported: it moved to the dependency-free sibling so specs mocking `@sg/common` can reach it.
export { groupAvailabilitySplitByCollateralType };

export const MINIMUM_LENDING_RATE = 11;

export interface ComponentInstance {
  ngOnDestroy?(): void;
  [key: string]: any;
}

export function enrichLocateMatches(
  matches: LocateMatch[],
  resetDefault = false,
  ignoreSettlementLocation = false,
  ignoreRateRequests = false,
  noRateLimit = false,
  options?: {
    formBuilder: FormBuilder;
    selectedSettlementLocation: 'All' | 'DOM' | 'ICSD';
    selectedLenderId?: string;
    userSession: SgEntityBasedSession;
    matchIdWithUpdatedRate?: string;
    existingLocateResult?: { matches: LocateMatch[] };
    cancelToken?: string;
    componentInstance?: ComponentInstance;
  }
): Array<LocateMatch> {
  return matches.map((match) =>
    enrichLocateMatch(match, resetDefault, ignoreSettlementLocation, ignoreRateRequests, noRateLimit, options)
  );
}

export function enrichLocateMatch(
  match: LocateMatch,
  resetDefault = false,
  ignoreSettlementLocation = false,
  ignoreRateRequests = false,
  noRateLimit = false,
  options?: {
    formBuilder: FormBuilder;
    selectedSettlementLocation: 'All' | 'DOM' | 'ICSD';
    selectedLenderId?: string;
    userSession: SgEntityBasedSession;
    matchIdWithUpdatedRate?: string;
    existingLocateResult?: { matches: LocateMatch[] };
    cancelToken?: string;
    componentInstance?: ComponentInstance;
  }
): LocateMatch {
  match.sharedUnits = getSharedUnitsBetweenCashAndNonCashCollateral(match.matchUnits);
  const collateralTypesGroups = groupAvailabilitySplitByCollateralType(match.matchUnits);
  if (options?.formBuilder) {
    match.cashMatch = getCollateralSpecificMatch(
      match,
      collateralTypesGroups.cash,
      CollateralTypeEnum.CASH,
      resetDefault,
      ignoreSettlementLocation,
      noRateLimit,
      options
    );

    match.nonCashMatch = getCollateralSpecificMatch(
      match,
      collateralTypesGroups.nonCash,
      CollateralTypeEnum.NONCASH,
      resetDefault,
      ignoreSettlementLocation,
      noRateLimit,
      options
    );

    if (!ignoreRateRequests) {
      processRateRequest(match, CollateralTypeEnum.CASH);
      processRateRequest(match, CollateralTypeEnum.NONCASH);
    }
  }

  return match;
}

export function processRateRequest(match: LocateMatch, collateralType: CollateralTypeEnum): void {
  const review = match.rateRequests?.reverse().find((review) => review.collateralType === collateralType);

  if (review) {
    const collateralMatch = collateralType === CollateralTypeEnum.CASH ? match.cashMatch : match.nonCashMatch;
    if (collateralMatch) {
      const rate =
        collateralType === CollateralTypeEnum.CASH
          ? convertBpsToPercentage(review.requestedRate)
          : review.requestedRate;
      collateralMatch.form.controls.units.setValue(review.units);
      collateralMatch.form.controls.rate.setValue(rate);
      collateralMatch.form.disable();
    }
  }
}

export function getSharedUnitsBetweenCashAndNonCashCollateral(
  matchAvailabilitySplit: BorrowerApi.AvailabilitySplitDto[]
): number {
  return matchAvailabilitySplit
    .filter((split) => isCashNonCashCollateralType(split.collateralType))
    .reduce((sharedUnits, split) => sharedUnits + split.units, 0);
}

export function getCollateralSpecificMatch(
  locateMatch: LocateMatch,
  csAvailabilitySplit: BorrowerApi.AvailabilitySplitDto[],
  collateralType: CollateralTypeDto,
  resetDefault = false,
  ignoreSettlementLocation = false,
  noRateLimit = false,
  options?: {
    formBuilder: FormBuilder;
    selectedSettlementLocation: 'All' | 'DOM' | 'ICSD';
    selectedLenderId?: string;
    userSession: SgEntityBasedSession;
    matchIdWithUpdatedRate?: string;
    existingLocateResult?: { matches: LocateMatch[] };
    cancelToken?: string;
    componentInstance?: ComponentInstance;
  }
): CollateralSpecificMatch | undefined {
  let csMatch: CollateralSpecificMatch;

  if (!csAvailabilitySplit?.length || !options?.formBuilder) {
    return undefined;
  }

  csMatch = {
    type: collateralType,
    matchUnits: groupAvailabilitySplitByBorrower(
      csAvailabilitySplit,
      options.selectedSettlementLocation,
      ignoreSettlementLocation,
      options.selectedLenderId
    ),
  };

  const defaultAvailabilitySplit = getDefaultAvailabilitySplit(
    csMatch,
    options.selectedSettlementLocation,
    options.selectedLenderId,
    options.userSession
  );

  csMatch.unitsRequestedQtyOverAvailable = +locateMatch.requestedUnits > +defaultAvailabilitySplit?.units;
  csMatch.requestedLowerRate = requestedLowerRate(locateMatch, options.matchIdWithUpdatedRate);
  csMatch.unitsRequestedValueBelowMin = locateMatch.isMinimumUnitsApplied;

  if (!options?.userSession?.user?.legalEntities || options.userSession.user.legalEntities?.length === 1) {
    csMatch.selectedLegalEntity = defaultAvailabilitySplit;
  }

  addLocateMatchForm(locateMatch, csMatch, defaultAvailabilitySplit, resetDefault, noRateLimit, {
    formBuilder: options.formBuilder,
    existingLocateResult: options.existingLocateResult,
    userSession: options.userSession,
    matchIdWithUpdatedRate: options.matchIdWithUpdatedRate,
    cancelToken: options.cancelToken,
    componentInstance: options.componentInstance,
  });

  return csMatch;
}

export function groupAvailabilitySplitByBorrower(
  matchAvailabilitySplit: BorrowerApi.AvailabilitySplitDto[],
  selectedSettlementLocation: 'All' | 'DOM' | 'ICSD',
  ignoreSettlementLocation = false,
  selectedLenderId?: string
): BorrowerApi.AvailabilitySplitDto[] {
  const availabilitySplitMap = new Map<string, BorrowerApi.AvailabilitySplitDto>();

  matchAvailabilitySplit
    .filter(
      (split) =>
        (split.settlementLocation === selectedSettlementLocation ||
          selectedSettlementLocation === 'All' ||
          ignoreSettlementLocation) &&
        (split.lenderId === selectedLenderId || !selectedLenderId)
    )
    .forEach((split) => {
      if (availabilitySplitMap.has(split.borrowerId)) {
        const borrowerSplit = availabilitySplitMap.get(split.borrowerId);
        borrowerSplit.units += split.units;
      } else {
        availabilitySplitMap.set(split.borrowerId, { ...split });
      }
    });

  return [...availabilitySplitMap.values()];
}

export function getDefaultAvailabilitySplit(
  match: CollateralSpecificMatch,
  selectedSettlementLocation: 'All' | 'DOM' | 'ICSD',
  selectedLenderId: string | undefined,
  userSession: SgEntityBasedSession
): BorrowerApi.AvailabilitySplitDto {
  const defaultBorrowerId =
    userSession?.user?.userSettings?.defaultLegalEntity ??
    (userSession?.entities?.length > 0 && userSession.entities[0] ? userSession.entities[0].id : undefined);
  const defaultAvailabilitySplit = match.matchUnits.find(
    (mu) =>
      defaultBorrowerId &&
      mu.borrowerId === defaultBorrowerId &&
      (mu.settlementLocation === selectedSettlementLocation || selectedSettlementLocation === 'All') &&
      (mu.lenderId === selectedLenderId || !selectedLenderId)
  );
  return defaultAvailabilitySplit ?? match.matchUnits[0];
}

export function requestedLowerRate(locateMatch: LocateMatch, matchIdWithUpdatedRate?: string): boolean {
  return (
    locateMatch.requestedLendingRate &&
    locateMatch.requestedLendingRate < locateMatch.lendingRate &&
    locateMatch.bbgid !== matchIdWithUpdatedRate
  );
}

export function addLocateMatchForm(
  locateMatch: BorrowerApi.LocateMatchDto,
  csMatch: CollateralSpecificMatch,
  defaultAvailabilitySplit: BorrowerApi.AvailabilitySplitDto,
  resetDefault = false,
  noRateLimit = false,
  options: {
    formBuilder: FormBuilder;
    existingLocateResult?: { matches: LocateMatch[] };
    userSession?: SgEntityBasedSession;
    matchIdWithUpdatedRate?: string;
    cancelToken?: string;
    componentInstance?: ComponentInstance;
  }
): void {
  const fb = options.formBuilder;
  const defaultUnits = defaultAvailabilitySplit?.units ?? 0;
  const isCash = isCashCollateral(csMatch);
  const defaultLendingRate = calculateDefaultLendingRateValue(locateMatch, isCash);

  let lendingRateValue = calculateLendingRateValue(
    locateMatch,
    defaultLendingRate,
    isCash,
    options.existingLocateResult
  );

  let unitsValue = resetDefault
    ? defaultUnits
    : calculateUnitsValue(locateMatch, csMatch, defaultUnits, options.existingLocateResult);

  const unitsValidators = Validators.compose([
    Validators.required,
    Validators.max(defaultUnits),
    Validators.min(locateMatch.minimumAvailableUnits),
  ]);

  const rateValidators = isCash
    ? []
    : Validators.compose([
        Validators.required,
        Validators.min(MINIMUM_LENDING_RATE),
        ...(noRateLimit ? [] : [Validators.max(defaultLendingRate)]),
      ]);

  const defaultKeyDates = getDefaultKeyDates(defaultAvailabilitySplit, { userSession: options.userSession });

  csMatch.form = fb.group({
    units: fb.nonNullable.control(defaultUnits, unitsValidators),
    rate: fb.nonNullable.control(defaultLendingRate, rateValidators),
    settlementDate: fb.nonNullable.control(defaultKeyDates),
  });

  const formControls = csMatch.form.controls;

  if (!options.existingLocateResult && defaultAvailabilitySplit?.units) {
    formControls.units.setValue(getAllocatedUnits(locateMatch, defaultAvailabilitySplit.units));
  } else {
    setFormControlValueAndMarkAsDirty(formControls.rate, lendingRateValue);
    setFormControlValueAndMarkAsDirty(formControls.units, unitsValue);
  }

  const cancelToken = options.cancelToken || 'defaultCancelToken';
  const componentInstance = options.componentInstance;
  if (formControls.units && componentInstance) {
    formControls.units.valueChanges
      .pipe(
        UnRx(componentInstance, cancelToken),
        tap(() => {
          csMatch.unitsRequestedQtyOverAvailable = false;
          csMatch.unitsRequestedValueBelowMin = false;

          if (formControls.units.dirty) {
            csMatch.unitsUpdated = false;
          }
        })
      )
      .subscribe((value) => {
        if (formControls.units.dirty) {
          calculateAffectedUnitsIfNeeded(
            value,
            locateMatch as LocateMatch,
            csMatch,
            false,
            options.formBuilder,
            cancelToken,
            componentInstance
          );
        }
      });
  }

  if (formControls.rate && componentInstance) {
    formControls.rate.valueChanges.pipe(UnRx(componentInstance, cancelToken)).subscribe(() => {
      const match = locateMatch as LocateMatch;
      if (match.cashMatch?.requestedLowerRate) {
        match.cashMatch.requestedLowerRate = false;
      }

      if (match.nonCashMatch?.requestedLowerRate) {
        match.nonCashMatch.requestedLowerRate = false;
      }

      if (csMatch.requestedLowerRate) {
        csMatch.requestedLowerRate = false;
      }
    });
  }
}

export function calculateDefaultLendingRateValue(locateMatch: BorrowerApi.LocateMatchDto, isCash: boolean): number {
  const cashLendingRate = convertBpsToPercentage(locateMatch.rebate);
  const nonCashLendingRate = locateMatch.lendingRate;

  return isCash ? cashLendingRate : nonCashLendingRate;
}

export function calculateLendingRateValue(
  locateMatch: BorrowerApi.LocateMatchDto,
  defaultLendingRate: number,
  isCash: boolean,
  existingLocateResult?: { matches: LocateMatch[] }
): number {
  let lendingRateValue = defaultLendingRate;

  if (existingLocateResult?.matches?.length > 0) {
    const match: LocateMatch = existingLocateResult.matches.find(
      (currentMatch) => currentMatch.bbgid === locateMatch.bbgid
    );

    if (match) {
      const { cashMatch, nonCashMatch } = match;
      const rateFormControl = isCash ? cashMatch?.form?.controls?.rate : nonCashMatch?.form?.controls?.rate;

      if (rateFormControl?.defaultValue === lendingRateValue) {
        lendingRateValue = rateFormControl.value;
      }
    }
  }

  return lendingRateValue;
}

export function calculateUnitsValue(
  locateMatch: BorrowerApi.LocateMatchDto,
  csMatch: CollateralSpecificMatch,
  defaultUnits: number,
  existingLocateResult?: { matches: LocateMatch[] }
): number {
  const isCash = isCashCollateral(csMatch);
  let unitsValue = defaultUnits;

  if (existingLocateResult?.matches?.length > 0) {
    const match: LocateMatch = existingLocateResult.matches.find((m) => m.bbgid === locateMatch.bbgid);

    if (match) {
      const { cashMatch, nonCashMatch } = match;
      const unitsFormControl = isCash ? cashMatch?.form?.controls?.units : nonCashMatch?.form?.controls?.units;
      if (unitsFormControl) {
        unitsValue = unitsFormControl.value;
        csMatch.unitsUpdated = unitsFormControl.defaultValue !== defaultUnits;
      }
    }
  }

  return unitsValue;
}

export function setFormControlValueAndMarkAsDirty(formControl: FormControl, value: any): void {
  formControl.setValue(value);
  formControl.markAsDirty();
}

export function getDefaultKeyDates(
  availabilitySplit: BorrowerApi.AvailabilitySplitDto,
  options?: {
    userSession?: SgEntityBasedSession;
  }
): BorrowerApi.KeyDatesDto {
  const marketDefaultKeyDates = availabilitySplit?.keyDates?.find((date) => date.isMarketDefault);

  if (!options?.userSession?.user?.userSettings?.defaultSettlementDate) {
    return marketDefaultKeyDates || availabilitySplit?.keyDates?.[0];
  }

  let defaultKeyDates = marketDefaultKeyDates;
  const userDefaultSettlementTerms = options.userSession.user.userSettings.defaultSettlementDate;

  if (userDefaultSettlementTerms) {
    const settlementTerms = Number(userDefaultSettlementTerms[1]);
    const userDefaultKeyDates = availabilitySplit?.keyDates?.find((date) => date.settlementTerms === settlementTerms);

    if (userDefaultKeyDates) {
      defaultKeyDates = userDefaultKeyDates;
    }
  }

  return defaultKeyDates;
}

export function getAllocatedUnits(locateMatch: LocateMatch, availableUnits: number): number {
  return locateMatch.requestedUnits && locateMatch.requestedUnits < availableUnits
    ? locateMatch.isMinimumUnitsApplied
      ? locateMatch.minimumAvailableUnits
      : locateMatch.requestedUnits
    : availableUnits;
}

export function calculateAffectedUnitsIfNeeded(
  changedValue: number,
  locateMatch: LocateMatch,
  csMatch: CollateralSpecificMatch,
  resetFieldAfterChange: boolean = false,
  formBuilder: FormBuilder,
  cancelToken: string,
  componentInstance?: ComponentInstance
): void {
  if (
    locateMatch.cashMatch &&
    locateMatch.nonCashMatch &&
    Number(changedValue) >= 0 &&
    Number(changedValue) <= csMatch.form.controls.units.defaultValue
  ) {
    const cashUnitsControl = locateMatch.cashMatch.form.controls.units;
    const cashUnitsWithoutShared = cashUnitsControl.defaultValue - locateMatch.sharedUnits;
    const nonCashUnitsControl = locateMatch.nonCashMatch.form.controls.units;
    const nonCashUnitsWithoutShared = nonCashUnitsControl.defaultValue - locateMatch.sharedUnits;

    if (isCashCollateral(csMatch)) {
      calculateAffectedControlUnits(
        changedValue,
        locateMatch,
        locateMatch.nonCashMatch,
        cashUnitsWithoutShared,
        nonCashUnitsControl,
        nonCashUnitsWithoutShared,
        resetFieldAfterChange,
        formBuilder,
        cancelToken,
        componentInstance
      );
    } else {
      calculateAffectedControlUnits(
        changedValue,
        locateMatch,
        locateMatch.cashMatch,
        nonCashUnitsWithoutShared,
        cashUnitsControl,
        cashUnitsWithoutShared,
        resetFieldAfterChange,
        formBuilder,
        cancelToken,
        componentInstance
      );
    }
  }
}

export function calculateAffectedControlUnits(
  changedValue: number,
  locateMatch: LocateMatch,
  collateralSpecificMatch: CollateralSpecificMatch,
  changedUnitsWithoutShared: number,
  affectedControl: FormControl,
  affectedUnitsWithoutShared: number,
  resetFieldAfterChange: boolean = false,
  formBuilder: FormBuilder,
  cancelToken: string,
  componentInstance?: ComponentInstance
): void {
  const { sharedUnits } = locateMatch;
  const changedUnits = Number(changedValue);
  const cashSharedUnitsUsed =
    changedUnits - changedUnitsWithoutShared > 0 ? changedUnits - changedUnitsWithoutShared : 0;
  const affectedSharedUnitsUsed =
    Number(affectedControl.value) - affectedUnitsWithoutShared > 0
      ? Number(affectedControl.value) - affectedUnitsWithoutShared
      : 0;
  const sharedUnitsLeft = sharedUnits - cashSharedUnitsUsed;
  if (
    affectedSharedUnitsUsed > sharedUnitsLeft ||
    (affectedControl.pristine && affectedSharedUnitsUsed < sharedUnitsLeft)
  ) {
    const maxUnits = affectedUnitsWithoutShared + sharedUnitsLeft;
    resetFieldAfterChange
      ? resetUnitsField(collateralSpecificMatch, locateMatch, maxUnits, formBuilder, cancelToken, componentInstance)
      : affectedControl.setValue(maxUnits);
  }
}

export function resetUnitsField(
  match: CollateralSpecificMatch,
  locateMatch: LocateMatch,
  updatedUnits: number,
  formBuilder: FormBuilder,
  cancelToken: string,
  componentInstance?: ComponentInstance
): void {
  const unitsValidators = Validators.compose([
    Validators.required,
    Validators.max(updatedUnits),
    Validators.min(locateMatch.minimumAvailableUnits),
  ]);
  const previousState = match.form.controls.units;
  match.form.controls.units = formBuilder.nonNullable.control(updatedUnits, unitsValidators);
  if (componentInstance) {
    UnRx.kill(componentInstance, cancelToken);
  }
  if (previousState.dirty) {
    match.form.controls.units.setValue(previousState.value);
    match.form.controls.units.markAsDirty();
  }
}
